import { ICustomField } from '@models/CustomField.model';

import {
  ArrowWrapper,
  ColumnContainer,
  ColumnName,
  Content,
  Direction,
  Divider,
  Heading,
  Line,
  LineText,
  NoText,
  Spacing,
} from '@pages/ConfigurationPage/Components/FieldsManagement/Components/AssetStatus/Components/CustomStatus/CustomStatus.styles';
import UpIcon from '@assets/svg/table-up.svg';
import DownIcon from '@assets/svg/table-down.svg';
import { DirectionCustomStatustypes } from '@pages/ConfigurationPage/Components/FieldsManagement/Components/AssetStatus/Components/CustomStatus/CustomStatus.query';
import { State } from '@hookstate/core';
import { SortCustomFieldTypes } from '../../CustomFieldsTab';
import { UsersTableListError } from './error/UsersTableList.error';
import { CustomFieldsTableLoading } from './loading/CustomFieldsTableLoading';
import { CustomFieldsMenu } from '../CustomFieldsMenu/CustomFieldsMenu';

interface IUserTableList {
  customFields?: ICustomField[];
  isLoading: boolean;
  isError: boolean;
  searchTextModel: string;
  directionDb: State<DirectionCustomStatustypes>;
  sort: State<SortCustomFieldTypes>;
}
interface IColumnTitle {
  label: string;
  direction?: 'up' | 'down';
  onClick?: () => void;
}
const ColumnTitle = ({ label, direction, ...rest }: IColumnTitle) => {
  return (
    <ColumnContainer {...rest}>
      <ColumnName>{label}</ColumnName>
      {!!direction && (
        <Direction>
          <img src={direction === 'up' ? UpIcon : DownIcon} alt="direction" />
        </Direction>
      )}
    </ColumnContainer>
  );
};

const CustomFieldsTable = ({
  customFields,
  isLoading,
  isError,
  searchTextModel,
  directionDb,
  sort,
}: IUserTableList) => {
  if (isLoading) {
    return <CustomFieldsTableLoading />;
  }
  if (isError || !customFields) {
    return <UsersTableListError />;
  }

  const customFieldFiltered = customFields.filter(
    item =>
      item.name
        .toLocaleLowerCase()
        .includes(searchTextModel.toLocaleLowerCase()) ||
      (item.description &&
        item.description
          .toLocaleLowerCase()
          .includes(searchTextModel.toLocaleLowerCase())) ||
      (item.type &&
        item.type
          .toLocaleLowerCase()
          .includes(searchTextModel.toLocaleLowerCase())),
  );

  const clickColumn = (column: SortCustomFieldTypes) => {
    if (sort.get() === column) {
      if (directionDb.get() === 'ASC') {
        directionDb.set('DESC');
        return;
      }
      if (directionDb.get() === 'DESC') {
        directionDb.set(undefined);
        return;
      }
      directionDb.set('ASC');
    } else {
      directionDb.set('ASC');
    }
    sort.set(column);
  };

  const directionResult = (
    column: SortCustomFieldTypes,
  ): 'up' | 'down' | undefined => {
    if (sort.get() === column) {
      if (directionDb.get() === 'ASC') {
        return 'up';
      }
      if (directionDb.get() === 'DESC') {
        return 'down';
      }
    }
    return undefined;
  };
  return (
    <>
      <Spacing>
        <Heading className="customField">
          <ArrowWrapper>
            <ColumnTitle
              label="Name"
              onClick={() => clickColumn('customField.name')}
              direction={directionResult('customField.name')}
            />
            {directionDb.get() === undefined && (
              <Direction>
                <img src={UpIcon} alt="" />
              </Direction>
            )}
          </ArrowWrapper>
          <ColumnTitle
            label="Description"
            onClick={() => clickColumn('customField.description')}
            direction={directionResult('customField.description')}
          />
          <ColumnTitle
            label="Type"
            onClick={() => clickColumn('customField.type')}
            direction={directionResult('customField.type')}
          />

          <ColumnTitle
            label="Values"
            onClick={() => clickColumn('selectable_value.value')}
            direction={directionResult('selectable_value.value')}
          />
          <ColumnTitle
            label="Default Value"
            onClick={() => clickColumn('customField.defaultValue')}
            direction={directionResult('customField.defaultValue')}
          />
          <ColumnTitle
            label="Onboard?"
            onClick={() => clickColumn('customField.showOnOnboard')}
            direction={directionResult('customField.showOnOnboard')}
          />
          <ColumnTitle
            label="Is Unique?"
            onClick={() => clickColumn('customField.isUnique')}
            direction={directionResult('customField.isUnique')}
          />
          <ColumnName>Is Searchable?</ColumnName>
          <div />
        </Heading>
      </Spacing>

      <Divider />
      <Content>
        {customFieldFiltered.map(item => (
          <Spacing key={item.id}>
            <Line className="customField">
              <LineText limitOfLines={1}>{item.name}</LineText>
              {item.description ? (
                <LineText limitOfLines={1}>{item.description}</LineText>
              ) : (
                <NoText>Empty</NoText>
              )}
              <LineText limitOfLines={1}>
                {item.type ? (
                  item.type.charAt(0).toUpperCase() + item.type.slice(1)
                ) : (
                  <NoText>N/A</NoText>
                )}
              </LineText>
              {item.type === 'select' && item.selectableValues.length > 0 ? (
                <LineText limitOfLines={1}>
                  {item.selectableValues.join(', ')}
                </LineText>
              ) : (
                <NoText>N/A</NoText>
              )}
              {item.defaultValue ? (
                <LineText limitOfLines={1}>{item.defaultValue}</LineText>
              ) : (
                <NoText>Empty</NoText>
              )}
              {item.showOnOnboard ? (
                <LineText limitOfLines={1}>Yes</LineText>
              ) : (
                <NoText>No</NoText>
              )}
              {item.isUnique ? (
                <LineText limitOfLines={1}>Yes</LineText>
              ) : (
                <NoText>No</NoText>
              )}
              {item.isSearchable ? (
                <LineText limitOfLines={1}>Yes</LineText>
              ) : (
                <NoText>No</NoText>
              )}
              <CustomFieldsMenu
                customField={item}
                customFields={customFields}
              />
            </Line>
          </Spacing>
        ))}
      </Content>
    </>
  );
};

export { CustomFieldsTable };
